import {ColorMapping} from '../interfaces/biozoom-api/app-configuration';

type RgbColor = [number, number, number];

const HEX_COLOR = /^#([0-9a-f]{6})$/i;

const parseHexColor = (color: string): RgbColor | undefined => {
  const match = color?.trim().match(HEX_COLOR);
  if (!match) {
    return undefined;
  }

  const value = match[1];
  return [
    Number.parseInt(value.slice(0, 2), 16),
    Number.parseInt(value.slice(2, 4), 16),
    Number.parseInt(value.slice(4, 6), 16),
  ];
};

const toHexColor = ([red, green, blue]: RgbColor): string =>
  `#${[red, green, blue].map(channel => Math.round(channel).toString(16).padStart(2, '0')).join('').toUpperCase()}`;

/**
 * Resolves a CSS color from a configurable value-to-color mapping.
 * Invalid mappings deliberately return undefined so the element's configured
 * static CSS remains untouched.
 */
export const getMappedColor = (value: number, mapping: ColorMapping): string | undefined => {
  if (!Number.isFinite(value) || !mapping || !Number.isFinite(mapping.min) || !Number.isFinite(mapping.max)
    || mapping.max <= mapping.min) {
    return undefined;
  }

  const stops = mapping.stops
    ?.map(stop => ({...stop, rgb: parseHexColor(stop.color)}))
    .filter(stop => Number.isFinite(stop.value) && stop.value >= mapping.min && stop.value <= mapping.max && !!stop.rgb)
    .sort((left, right) => left.value - right.value);

  if (!stops?.length) {
    return undefined;
  }

  const clampedValue = Math.min(mapping.max, Math.max(mapping.min, value));
  const before = [...stops].reverse().find(stop => stop.value <= clampedValue) ?? stops[0];

  if (mapping.mode === 'discrete') {
    return toHexColor(before.rgb);
  }

  const after = stops.find(stop => stop.value >= clampedValue) ?? stops[stops.length - 1];
  if (before.value === after.value) {
    return toHexColor(before.rgb);
  }

  const progress = (clampedValue - before.value) / (after.value - before.value);
  return toHexColor(before.rgb.map((channel, index) => channel + (after.rgb[index] - channel) * progress) as RgbColor);
};
